/**
 * Форматтеры и i18n-лейблы модуля «Анонсы» (мероприятия отеля).
 *
 * Категории и состояния — закрытые таксономии бэка
 * (internal/events/domain.EventCategory / EventState). Сырые англ-коды
 * НЕ показываем (feedback_build_to_design_not_api_i18n_mobile): мапим в
 * русские лейблы, тон — для бейджей (fleet-badge--*).
 */
import type { EventCategory, EventState } from '@hsc/types';

/** Категория события → русский лейбл (дизайн manager v3 §12.2 type chips). */
export const EVENT_CATEGORY_LABEL: Readonly<Record<EventCategory, string>> = {
  party: 'Вечеринка',
  lecture: 'Лекция',
  kids: 'Детям',
  sport: 'Спорт',
  spa: 'Спа',
  general: 'Общее',
  excursion: 'Экскурсия',
};

/** Состояние события → русский лейбл. */
export const EVENT_STATE_LABEL: Readonly<Record<EventState, string>> = {
  draft: 'Черновик',
  published: 'Опубликовано',
  hidden: 'Скрыто',
  deleted: 'Удалено',
};

/** Состояние → тон бейджа (fleet-badge--*). */
export const EVENT_STATE_TONE: Readonly<Record<EventState, string>> = {
  draft: 'muted',
  published: 'ok',
  hidden: 'warn',
  deleted: 'danger',
};

/** ISO → «02.06.2026 19:30» (локаль ru). «—» при невалидной дате. */
export function formatDateTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** ISO → «19:30» (только время, для строки события). */
export function formatTime(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/** Бейдж даты для карточки: { day: «02», month: «июн» }. */
export function dateBadge(iso: string | undefined): { day: string; month: string } {
  if (!iso) return { day: '—', month: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: '—', month: '' };
  return {
    day: d.toLocaleDateString('ru-RU', { day: '2-digit' }),
    month: d.toLocaleDateString('ru-RU', { month: 'short' }).replace('.', ''),
  };
}

/**
 * Значение для <input type="datetime-local"> из ISO (локальное время без TZ).
 * Возвращает «» при невалидной дате.
 */
export function isoToLocalInput(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60_000);
  return local.toISOString().slice(0, 16);
}

/** datetime-local → RFC3339 (UTC, с «Z») для отправки на бэк. «» → «». */
export function localInputToISO(local: string): string {
  if (!local) return '';
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
}

/** Группа карточки для фильтр-чипов: сегодня / предстоящие / архив. */
export type EventBucket = 'today' | 'upcoming' | 'past';

/** К какой группе относится событие по starts_at (относительно now). */
export function bucketOf(startsAt: string, now: Date = new Date()): EventBucket {
  const s = new Date(startsAt);
  if (Number.isNaN(s.getTime())) return 'past';
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday = new Date(startOfToday.getTime() + 24 * 3600_000);
  if (s < startOfToday) return 'past';
  if (s < endOfToday) return 'today';
  return 'upcoming';
}

/**
 * Текст занятости мест по дизайну guest v7 §9: «8 из 12 мест занято · 4
 * свободно». Без вместимости — «N записались». going — занято, capacity —
 * вместимость (если задана).
 */
export function seatsLabel(going: number, capacity: number | undefined): string {
  if (capacity === undefined || capacity <= 0) {
    return going > 0 ? `${going} ${plural(going, 'записался', 'записались', 'записались')}` : 'Записей пока нет';
  }
  const free = Math.max(0, capacity - going);
  return `${going} из ${capacity} ${plural(capacity, 'место', 'места', 'мест')} занято · ${free} свободно`;
}

/**
 * Цена события по дизайну §12: «Бесплатно» при отсутствии цены, иначе
 * «3 500 ₽» (копейки → рубли, локаль ru). Валюта RUB → «₽», иначе ISO-код.
 */
export function formatPrice(priceKopecks: number | undefined, currency?: string): string {
  if (priceKopecks === undefined || priceKopecks <= 0) return 'Бесплатно';
  const major = priceKopecks / 100;
  const symbol = !currency || currency === 'RUB' ? ' ₽' : ` ${currency}`;
  return `${major.toLocaleString('ru-RU')}${symbol}`;
}

/** Русское склонение по числу (1 место / 2 места / 5 мест). */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
